/**
 * UnauthorizedPage component - displays when user lacks required permissions
 *
 * Provides a user-friendly message when access is denied due to insufficient roles.
 * Includes navigation back to accessible areas.
 *
 * Validates: Requirements 3.4, 3.5
 */

import React from 'react';
import { MessageBar, MessageBarType, PrimaryButton, Stack, Text } from '@fluentui/react';
import { useAuth } from '../auth/useAuth';

export interface UnauthorizedPageProps {
  /** Custom message to display instead of default */
  message?: string;
  /** Custom action button text */
  actionText?: string;
  /** Custom action to perform when button is clicked */
  onAction?: () => void;
}

/**
 * Page displayed when user is authenticated but lacks required permissions
 *
 * Shows a clear message about insufficient permissions and provides
 * options to navigate to accessible areas or contact support.
 */
export const UnauthorizedPage: React.FC<UnauthorizedPageProps> = ({
  message,
  actionText = 'Go to Dashboard',
  onAction,
}) => {
  const { user, logout } = useAuth();

  const handleDefaultAction = () => {
    // Navigate to root/dashboard by default
    window.location.href = '/';
  };

  const handleAction = onAction || handleDefaultAction;

  return (
    <div
      data-testid="unauthorized"
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        padding: '20px',
        backgroundColor: '#faf9f8',
      }}
    >
      <Stack
        tokens={{ childrenGap: 20 }}
        styles={{
          root: {
            maxWidth: '500px',
            padding: '40px',
            backgroundColor: 'white',
            borderRadius: '8px',
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
          },
        }}
      >
        <MessageBar messageBarType={MessageBarType.warning}>
          <strong>Access Denied</strong>
        </MessageBar>

        <Stack tokens={{ childrenGap: 16 }}>
          <Text variant="xLarge" styles={{ root: { fontWeight: 600 } }}>
            Insufficient Permissions
          </Text>

          <Text variant="medium">
            {message ||
              'You do not have the required permissions to access this area of the application.'}
          </Text>

          {user && (
            <Text variant="small" styles={{ root: { color: '#605e5c' } }}>
              Signed in as: {user.name} ({user.email})
              <br />
              Your roles: {user.roles.length > 0 ? user.roles.join(', ') : 'None assigned'}
            </Text>
          )}

          <Stack horizontal tokens={{ childrenGap: 12 }} styles={{ root: { marginTop: '20px' } }}>
            <PrimaryButton text={actionText} onClick={handleAction} />
            <PrimaryButton
              text="Sign Out"
              onClick={() => logout()}
              styles={{ root: { backgroundColor: '#d13438' } }}
            />
          </Stack>

          <Text variant="small" styles={{ root: { color: '#605e5c', marginTop: '16px' } }}>
            If you believe you should have access to this area, please contact your administrator.
          </Text>
        </Stack>
      </Stack>
    </div>
  );
};
